import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Pencil, Trash2, Tags, Search, Package, ImageOff, Layers, Tag, X, Check, GripVertical, Scale, FileUp, Copy, Loader2 } from "lucide-react";
import { useDragSort, arrayMove } from "@/lib/useDragSort";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { LabelsDialog } from "@/components/stock/LabelsDialog";
import { ScaleLabelDialog } from "@/components/stock/ScaleLabelDialog";
import { MenuImportDialog } from "@/components/product/import/MenuImportDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, EmptyState } from "@/components/common";
import { ProductForm, EMPTY_PRODUCT, productRouting } from "@/components/product/ProductForm";
import { uploadImage } from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, canDo, imgUrl, fmtQty } from "@/lib/api";
import { toast } from "sonner";

function CategoryPhoto({ c, run }) {
  const save = (image_url) => run(() => api.put(`/categories/${c.id}`, { name: c.name, order: c.order || 0, image_url }));
  const pick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(async () => save(await uploadImage(f))); };
  return (
    <span className="flex items-center gap-2">
      {c.image_url ? <img src={imgUrl(c.image_url)} alt={c.name} className="h-8 w-8 rounded object-cover" /> : <span className="grid h-8 w-8 place-items-center rounded bg-muted"><ImageOff className="h-4 w-4 text-muted-foreground" /></span>}
      <label className="cursor-pointer text-xs text-primary hover:underline" data-testid={`category-photo-${c.id}`}>{c.image_url ? "Trocar foto" : "Foto"}<input type="file" accept="image/*" className="hidden" onChange={pick} /></label>
      {c.image_url && <button className="text-xs text-muted-foreground hover:text-red-500" onClick={() => save("")} data-testid={`category-photo-remove-${c.id}`}>Remover</button>}
    </span>
  );
}

const Grip = ({ testId }) => <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground active:cursor-grabbing" data-testid={testId} />;

function CategoryRow({ c, run, canDelete, drag }) {
  const [name, setName] = useState(null);
  const d = drag(c.id, name === null);
  const save = () => name.trim() && run(async () => { await api.put(`/categories/${c.id}`, { name: name.trim(), order: c.order || 0, image_url: c.image_url || "" }); setName(null); });
  return (
    <li {...d} className={`flex items-center justify-between gap-2 bg-card px-3 py-2 text-sm transition-colors ${d.className || ""}`} data-testid={`category-row-${c.id}`}>
      {name === null ? (<>
        <Grip testId={`category-drag-${c.id}`} />
        <span className="flex-1">{c.name}</span>
        <button onClick={() => setName(c.name)} title="Editar nome" data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>
      </>) : (<>
        <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setName(null); }} className="h-8 flex-1" data-testid={`category-name-input-${c.id}`} />
        <button onClick={save} disabled={!name.trim()} title="Salvar" data-testid={`category-save-${c.id}`}><Check className="h-4 w-4 text-emerald-500" /></button>
        <button onClick={() => setName(null)} title="Cancelar" data-testid={`category-cancel-${c.id}`}><X className="h-4 w-4 text-muted-foreground" /></button>
      </>)}
      <CategoryPhoto c={c} run={run} />
      {canDelete && <button onClick={() => run(() => api.delete(`/categories/${c.id}`))} data-testid={`delete-category-${c.id}`}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-500" /></button>}
    </li>
  );
}

function CategoriesDialog({ open, onOpenChange, categories, reload, canDelete }) {
  const [name, setName] = useState("");
  const [list, setList] = useState(categories);
  useEffect(() => setList(categories), [categories]);
  const run = async (fn) => {
    try {
      await fn();
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const move = (from, to) => {
    const ids = list.map((c) => c.id);
    const next = arrayMove(list, ids.indexOf(from), ids.indexOf(to));
    setList(next);
    run(() => api.post("/categories/reorder", { ids: next.map((c) => c.id) }));
  };
  const drag = useDragSort(list.map((c) => c.id), move);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="categories-modal">
        <DialogHeader><DialogTitle>Categorias</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Arraste pela alça para mudar a ordem (vale no cardápio, PDV e app de comandas). A foto aparece como fundo do botão no app de comandas do celular.</p>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nova categoria" data-testid="new-category-input" />
          <Button onClick={() => run(async () => { await api.post("/categories", { name, order: categories.length }); setName(""); })} disabled={!name} data-testid="add-category-btn">Adicionar</Button>
        </div>
        <ul className="max-h-[55vh] divide-y overflow-y-auto rounded-lg border" data-testid="categories-list">
          {list.map((c) => <CategoryRow key={c.id} c={c} run={run} canDelete={canDelete} drag={drag} />)}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

function ProductRow({ p, catName, canEdit, canDelete, onEdit, onDelete, onDuplicate, copying, selected, onToggle, drag }) {
  const low = p.stock != null && p.stock <= (p.min_stock || 0);
  const d = drag(p.id, canEdit);
  return (
    <TableRow {...d} data-testid={`product-row-${p.id}`} className={`${p.active ? "" : "opacity-50"} ${d.className || ""}`}>
      {canEdit && <TableCell className="w-8 pr-0" title="Arraste para reordenar"><Grip testId={`product-drag-${p.id}`} /></TableCell>}
      {onToggle && <TableCell className="w-10"><Checkbox checked={selected} onCheckedChange={() => onToggle(p.id)} data-testid={`product-select-${p.id}`} /></TableCell>}
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-muted grid place-items-center">{p.image_url ? <img src={imgUrl(p.image_url)} alt={p.name} className="h-full w-full object-cover" /> : <ImageOff className="h-4 w-4 text-muted-foreground/50" />}</div>
          <div className="min-w-0"><p className="font-medium">{p.name}</p>
            <p className="text-xs text-muted-foreground">{[p.product_type === "combo" && "Combo", p.option_groups?.length && `${p.option_groups.length} grupo(s) de opções`, p.recipe?.length && "Ficha técnica", !p.active && "Inativo"].filter(Boolean).join(" · ")}</p></div>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">{catName[p.category_id] || "Geral"}{p.subcategory ? ` / ${p.subcategory}` : ""}</TableCell>
      <TableCell className="font-mono text-xs">{p.internal_code || "—"}</TableCell>
      <TableCell className="font-mono text-xs" data-testid={`product-barcode-${p.id}`}>{p.barcode || "—"}</TableCell>
      <TableCell>{p.stock == null ? "—" : <span className={low ? "font-semibold text-red-500" : ""} data-testid={`product-stock-${p.id}`}>{fmtQty(p.stock)} {p.unit || "un"}</span>}</TableCell>
      <TableCell className="text-right font-mono">{brl(p.price)}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {canEdit && <Button size="icon" variant="ghost" onClick={() => onEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>}
        {canEdit && <Button size="icon" variant="ghost" title="Duplicar produto" disabled={!!copying} onClick={() => onDuplicate(p)} data-testid={`duplicate-product-${p.id}`}>{copying === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}</Button>}
        {canDelete && <Button size="icon" variant="ghost" onClick={() => onDelete(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </TableCell>
    </TableRow>
  );
}

export default function Products() {
  const [data, setData] = useState({ products: [], categories: [], ingredients: [] });
  const [edit, setEdit] = useState(null);
  const [cats, setCats] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const { session } = useAuth();
  const canEdit = canDo(session.user, "edit_products");
  const canDelete = canDo(session.user, "delete_records");
  const canLabel = canEdit || canDo(session.user, "change_stock");
  const [sel, setSel] = useState(() => new Set());
  const [labels, setLabels] = useState(false);
  const [scale, setScale] = useState(false);
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const sectors = session.tenant.settings.production_sectors || [];
  const load = useCallback(() => Promise.all([api.get("/products"), api.get("/categories"), api.get("/ingredients").catch(() => ({ data: [] }))]).then(([p, c, i]) => setData({ products: p.data, categories: c.data, ingredients: i.data })), []);
  useEffect(() => { load(); }, [load]);
  const { products, categories, ingredients } = data;
  const catName = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.name])), [categories]);
  const s = q.toLowerCase();
  const list = products.filter((p) => (!cat || p.category_id === cat) && (p.name.toLowerCase().includes(s) || (p.barcode || "").includes(q) || (p.internal_code || "").toLowerCase().includes(s)));
  const open = (p) => setEdit({ ...EMPTY_PRODUCT, ...p, track_stock: p.stock != null, stock: p.stock ?? "", category_id: p.category_id || "geral", ...productRouting(p, sectors), option_groups: (p.option_groups || []).map((g) => ({ ...g, max: g.max ?? (g.type === "single" ? 1 : "") })) });
  const move = async (from, to) => {
    const ids = products.map((p) => p.id);
    const next = arrayMove(products, ids.indexOf(from), ids.indexOf(to)).map((p, i) => ({ ...p, display_order: i }));
    setData((d) => ({ ...d, products: next }));
    try { await api.post("/products/reorder", { ids: next.map((p) => p.id) }); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const drag = useDragSort(list.map((p) => p.id), move);
  const remove = async (p) => {
    if (!window.confirm(`Excluir ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [copying, setCopying] = useState(null);
  const duplicate = async (p) => {
    setCopying(p.id);
    try {
      const { data: c } = await api.post(`/products/${p.id}/duplicate`);
      await load();
      toast.success(`Cópia criada: ${c.name}`, { action: { label: "Editar", onClick: () => open(c) } });
    } catch (e) { toast.error(errMsg(e)); } finally { setCopying(null); }
  };
  const allSel = list.length > 0 && list.every((p) => sel.has(p.id));
  const toggleAll = () => setSel((s) => { const n = new Set(s); list.forEach((p) => (allSel ? n.delete(p.id) : n.add(p.id))); return n; });
  const openLabels = () => (sel.size > 500 ? toast.error("Selecione no máximo 500 produtos por vez") : setLabels(true));
  const [importing, setImporting] = useState(false);

  return (
    <>
      <PageHeader eyebrow="Catálogo" title="Produtos" subtitle={`${products.length} produtos · ${products.filter((p) => p.stock != null && p.stock <= (p.min_stock || 0)).length} com estoque baixo`}
        actions={<>
          {canLabel && sel.size > 0 && <>
            <Button variant="ghost" onClick={() => setSel(new Set())} data-testid="products-clear-selection-btn"><X className="h-4 w-4 mr-1" />Limpar seleção</Button>
            <Button variant="outline" onClick={openLabels} data-testid="products-print-labels-btn"><Tag className="h-4 w-4 mr-1" />Imprimir etiquetas ({sel.size})</Button>
          </>}
          {canLabel && <Button variant="outline" onClick={() => setScale(true)} data-testid="scale-label-btn"><Scale className="h-4 w-4 mr-1" />Etiqueta de balança</Button>}
          {canEdit && <>
          <Button variant="outline" onClick={() => setCats(true)} data-testid="manage-categories-btn"><Tags className="h-4 w-4 mr-1" />Categorias</Button>
          <Button variant="outline" onClick={() => setImporting(true)} data-testid="import-menu-btn"><FileUp className="h-4 w-4 mr-1" />Importar cardápio</Button>
          <Button onClick={() => setEdit({ ...EMPTY_PRODUCT, display_order: products.length })} data-testid="new-product-btn"><Plus className="h-4 w-4 mr-1" />Novo produto</Button>
          </>}
        </>} />
      <div className="flex flex-wrap gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou código" className="pl-9" data-testid="products-search-input" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-md border bg-transparent px-3 text-sm" data-testid="products-category-filter">
          <option value="">Todas as categorias</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <Badge variant="outline" className="h-10 gap-1 px-3"><Layers className="h-3 w-3" />{list.length} exibidos</Badge>
      </div>
      {!products.length ? <EmptyState icon={Package} title="Nenhum produto" text="Cadastre seus produtos para começar a vender." /> : (
        <div className="card-surface overflow-x-auto">
          <Table data-testid="products-table">
            <TableHeader><TableRow>{canEdit && <TableHead className="w-8 pr-0" />}{canLabel && <TableHead className="w-10"><Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="products-select-all" /></TableHead>}<TableHead>Produto</TableHead><TableHead>Categoria</TableHead><TableHead>Código interno</TableHead><TableHead>Código de barras</TableHead><TableHead>Estoque</TableHead><TableHead className="text-right">Preço</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>{list.map((p) => <ProductRow key={p.id} p={p} catName={catName} canEdit={canEdit} canDelete={canDelete} onEdit={open} onDelete={remove} onDuplicate={duplicate} copying={copying} selected={sel.has(p.id)} onToggle={canLabel ? toggle : null} drag={drag} />)}</TableBody>
          </Table>
        </div>
      )}
      {edit && <ProductForm key={edit.id || "new"} product={edit} onExisting={(id) => { const p = products.find((x) => x.id === id); if (p) open(p); }} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} categories={categories} products={products} ingredients={ingredients} sectors={sectors} />}
      {importing && <MenuImportDialog onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); }} />}
      <CategoriesDialog open={cats} onOpenChange={setCats} categories={categories} reload={load} canDelete={canDelete} />
      {scale && <ScaleLabelDialog products={products.filter((p) => p.unit === "kg" && p.active !== false)} onClose={() => { setScale(false); load(); }} />}
      {labels && <LabelsDialog title={`Etiquetas de ${sel.size} produto(s)`} lines={[...sel].map((id) => ({ item_id: id, qty: 1 }))} allowStock onClose={() => { setLabels(false); load(); }} />}
    </>
  );
}
